import { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader2, Eye, EyeOff, ShieldAlert } from "lucide-react";

export default function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const suspended = searchParams.get("suspended") === "1";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const r = await login(email.trim(), password);
    setLoading(false);
    if (r.ok) nav("/dashboard");
    else setError(r.error);
  };

  return (
    <div className="min-h-screen bg-[#F1F5F9]">
      <div className="flex min-h-screen flex-col items-center justify-center px-4 py-10">
        <div className="w-full max-w-md">
          <div className="mb-6 flex flex-col items-center text-center">
            <div className="mb-4 flex h-24 w-24 items-center justify-center rounded-2xl bg-white p-2 shadow-lg sm:h-28 sm:w-28">
              <img
                src="/assets/logo.svg"
                alt="Ciudad Enfermeria San Luis Potosi"
                className="h-full w-full object-contain"
              />
            </div>
            <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
              Ciudad Enfermeria
            </h1>
            <p className="mt-1 text-sm font-medium text-slate-500">
              Programa de Cuidados de Enfermeria
            </p>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
            <h2 className="mb-1 text-xl font-bold text-slate-900">Iniciar sesion</h2>
            <p className="mb-6 text-sm text-slate-500">
              Ingresa con tu correo y contrasena
            </p>

            {suspended && (
              <div
                className="mb-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm font-medium text-red-800"
                data-testid="login-suspended-banner"
              >
                <ShieldAlert className="mt-0.5 h-4 w-4 flex-shrink-0" />
                <span>
                  Tu acceso fue suspendido. Contacta al coordinador de Ciudad Enfermeria.
                </span>
              </div>
            )}

            <form onSubmit={onSubmit} className="space-y-4">
              <div>
                <Label htmlFor="email" className="mb-1.5 block text-sm font-semibold text-slate-700">
                  Correo electronico
                </Label>
                <Input
                  id="email"
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="tu@correo.com"
                  className="h-12 text-base"
                  data-testid="login-email-input"
                />
              </div>
              <div>
                <Label htmlFor="password" className="mb-1.5 block text-sm font-semibold text-slate-700">
                  Contrasena
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPass ? "text" : "password"}
                    required
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="******"
                    className="h-12 pr-12 text-base"
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPass((s) => !s)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-2 text-slate-500 hover:bg-slate-100"
                    data-testid="login-toggle-password"
                  >
                    {showPass ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              {error && (
                <div
                  className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm font-medium text-red-700"
                  data-testid="login-error"
                >
                  {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                className="h-12 w-full rounded-xl bg-[#1D4ED8] text-base font-semibold hover:bg-[#1E40AF]"
                data-testid="login-submit-btn"
              >
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Entrar"}
              </Button>
            </form>

            <div className="mt-6 text-center text-sm text-slate-600">
              No tienes cuenta?{" "}
              <Link
                to="/register"
                className="font-semibold text-[#1D4ED8] hover:underline"
                data-testid="login-register-link"
              >
                Registrate aqui
              </Link>
            </div>
          </div>

          <p className="mt-6 text-center text-xs text-slate-400">
            Acceso seguro - tus datos estan resguardados
          </p>
        </div>
      </div>
    </div>
  );
}
